import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionKeys } from '@/features/subscriptions/hooks/keys';
import { provider as providerApi } from '@/shared/api/shop';
import { subscription as subscriptionApi } from '@/shared/api/subscription';
import { AUTH_TIMEOUTS } from '@/shared/config/constants';
import { useAuthStore } from '@/shared/store/auth/useAuthStore';
import type { User, ShopSubscription, Provider , UserRole } from '@/shared/types';
import { logger } from '@/shared/utils/logger';
import { prepareUserData } from './auth.utils';
import { authKeys } from './keys';
import { useApiHelper } from './useApiHelpers';
import {
  useAuth0Integration,
  type GetNewTokenOptions,
} from './useAuth0Integration';
import { usePreferredLocaleSync } from './usePreferredLocaleSync';
import { useRoleHelpers } from './useRoleHelpers';
import { useUserIdentifiers } from './useUserIdentifiers';

/**
 * Transform database role (UPPER_SNAKE_CASE) to frontend role (snake_case)
 */
const transformDatabaseRole = (dbRole: string): UserRole | null => {
  const mapping: Record<string, UserRole> = {
    INDEPENDENT_PROVIDER: 'independent_provider',
    SHOP_OWNER: 'shop_owner',
    SHOP_MANAGER: 'shop_manager',
    STAFF_PROVIDER: 'staff_provider',
  };
  return mapping[dbRole] || null;
};

/**
 * Interface for the centralized auth hook return values
 */
export interface UseAuthReturn {
  user: User | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    context?: 'client' | 'provider' | 'staff',
    inviteCode?: string,
  ) => Promise<void>;
  logout: () => void;
  error: Error | null | undefined;

  // Safe user identifiers with built-in null handling
  userId: string; // Returns empty string if null
  providerId: string; // Returns empty string if null
  businessOwnerId: string; // Returns empty string if null

  // Original nullable IDs if explicit null checking is needed
  rawUserId: string | null;
  rawProviderId: string | null;
  rawBusinessOwnerId: string | null;

  // Role-based utility functions
  isProvider: boolean;
  isShopOwner: boolean;
  isCustomer: boolean;

  // Access token for API calls
  accessToken: string | null;

  // Safe API request helper
  withAuth: <T>(
    fetchFn: (token: string, id: string) => Promise<T>,
    idType?: 'user' | 'provider' | 'shop',
  ) => Promise<T | null>;

  // Refresh user data and role from Auth0
  refreshUser: () => Promise<boolean>;

  // Get a new access token - returns the token string directly
  // Used by axios interceptor for 401 retry logic
  getNewToken: (options?: GetNewTokenOptions) => Promise<string | null>;

  // Subscription data (pre-fetched for route guards)
  currentSubscription: ShopSubscription | null;
  hasActiveSubscription: boolean;
  isSubscriptionLoading: boolean;
  subscriptionError: Error | null;
}

/**
 * A centralized hook that combines Auth0 authentication and user role detection
 * with additional utilities for accessing user identifiers
 */
const useAuth = (): UseAuthReturn => {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<UserRole | null>(null);

  const {
    accessToken,
    initialRole,
    auth0User,
    auth0IsAuthenticated,
    auth0Loading,
    auth0Error,
    login,
    logout: auth0Logout,
    refreshToken,
    getNewToken,
  } = useAuth0Integration();

  const {
    user: storeUser,
    isAuthenticated: storeIsAuthenticated,
    setUser,
    setIsAuthenticated,
    setIsLoading,
    logout: storeLogout,
  } = useAuthStore();

  // Check if we're in checkout flow - if so, skip provider query to prevent 401 race condition
  // Auth0 may not have fully initialized the token after Stripe redirect
  const isInCheckoutFlow = !!useAuthStore.getState().checkoutSessionId;

  // Fetch provider data from database (source of truth for role)
  // IMPORTANT: Pass accessToken directly to avoid race condition with axios interceptor configuration
  const { data: providerData, isLoading: isProviderLoading } =
    useQuery<Provider | null>({
      queryKey: authKeys.currentProvider(auth0User?.sub),
      queryFn: async () => {
        try {
          // Pass token directly to ensure it's used immediately, bypassing axios interceptor timing issues
          return await providerApi.getCurrent(accessToken);
        } catch (error) {
          // Provider not found is normal for non-provider users (e.g., clients)
          logger.debug('Provider not found for current user - likely a client', { error });
          return null;
        }
      },
      // Disable during checkout flow to prevent 401 race condition with Auth0
      enabled: auth0IsAuthenticated && !!accessToken && !isInCheckoutFlow,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: false, // Don't retry - absence of provider is valid for clients
      refetchOnWindowFocus: false,
    });

  // Determine role: prefer database role, fall back to checkout role, then JWT role
  const databaseRole = providerData?.role
    ? transformDatabaseRole(providerData.role)
    : null;

  // Get pending role from checkout flow (stored when verify-checkout-session succeeds)
  const pendingProviderRole = useAuthStore.getState().pendingProviderRole;
  const checkoutRole = pendingProviderRole
    ? transformDatabaseRole(pendingProviderRole)
    : null;

  // Update role from database, checkout flow, or fallback to Auth0 JWT
  useEffect(() => {
    if (databaseRole) {
      // Database role takes precedence - this is the source of truth
      setRole(databaseRole);
    } else if (checkoutRole) {
      // Use checkout role during checkout flow (before provider query runs)
      setRole(checkoutRole);
    } else if (initialRole && !isProviderLoading) {
      // Fall back to JWT role only when provider loading is complete
      // and no database role was found (e.g., for clients)
      setRole(initialRole);
    }
  }, [databaseRole, checkoutRole, initialRole, isProviderLoading]);

  // Update user in store when auth0User changes
  useEffect(() => {
    if (auth0IsAuthenticated && auth0User) {
      const userData = prepareUserData(auth0User);
      setUser(userData);
    }
  }, [auth0IsAuthenticated, auth0User, setUser]);

  // Update loading state based on Auth0 loading
  useEffect(() => {
    setIsLoading(auth0Loading);
  }, [auth0Loading, setIsLoading]);

  // Update authentication state
  // IMPORTANT: Don't update auth state while Auth0 is still loading
  // This preserves the persisted state during initialization (e.g., page refresh)
  useEffect(() => {
    if (auth0Loading) {
      // Auth0 still initializing - preserve persisted state
      return;
    }

    setIsAuthenticated(auth0IsAuthenticated);

    if (!auth0IsAuthenticated && storeUser) {
      setUser(null);
      setRole(null);
    }
  }, [auth0IsAuthenticated, auth0Loading, setIsAuthenticated, setUser, storeUser]);

  // Let Auth0 finish initialization instead of clearing a persisted session on
  // a fixed timer. Mobile browsers can pause timers and network while sleeping,
  // and staff users can briefly sit in this state while role/profile data
  // catches up. Definitive unauthenticated state is handled by the effect above;
  // failed token refreshes are handled by AxiosAuthProvider.
  useEffect(() => {
    // Get checkout session ID from store - if present, we're returning from Stripe
    const { checkoutSessionId } = useAuthStore.getState();

    if (checkoutSessionId) {
      logger.debug(
        'Checkout session detected - preserving auth state during Auth0 initialization',
      );
    }
  }, [auth0Loading]);

  // Get user identifiers
  const {
    rawUserId,
    rawProviderId,
    rawBusinessOwnerId,
    userId,
    providerId,
    businessOwnerId,
  } = useUserIdentifiers({
    isAuthenticated: auth0IsAuthenticated,
    userSub: auth0User?.sub,
    role,
  });

  // Get role helper functions
  const { isProvider, isShopOwner, isCustomer } = useRoleHelpers(role);

  // Keep language preference synced between the server profile and i18n:
  // register the sync target for setLanguage(), and apply the server's
  // preferredLocale once on load (server wins over localStorage when authed).
  usePreferredLocaleSync({
    isProvider,
    providerId: providerData?.id ?? null,
    userId: auth0User?.sub ?? null,
    providerPreferredLocale: providerData?.preferredLocale,
  });

  // Get API helper function
  const { withAuth } = useApiHelper({
    accessToken,
    rawUserId,
    rawProviderId,
    rawBusinessOwnerId,
  });

  // Determine if subscription check is needed (for shop_owner, shop_manager, and independent_provider)
  const needsSubscriptionCheck =
    auth0IsAuthenticated &&
    role !== null &&
    ['shop_owner', 'shop_manager', 'independent_provider'].includes(role);

  // Use user.sub as provider ID for subscription lookup
  const providerAuthId = auth0User?.sub || '';

  // Pre-fetch subscription data at auth level for route guards
  // This allows guards to access subscription status synchronously
  const {
    data: subscriptionData,
    isLoading: isSubscriptionLoading,
    error: subscriptionError,
  } = useQuery<ShopSubscription[] | null>({
    queryKey: subscriptionKeys.activeProviderSubscription(providerAuthId),
    queryFn: async () => {
      if (!providerAuthId) return null;
      try {
        const result = await subscriptionApi.getProviderSubscriptions(
          providerAuthId,
          true, // Only fetch active subscriptions
          accessToken || undefined,
        );
        return result;
      } catch (error) {
        logger.error('Failed to fetch subscription in auth context:', error);
        // Return null on error - fail open to prevent blocking users
        return null;
      }
    },
    enabled: needsSubscriptionCheck && !!providerAuthId && !!accessToken,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: AUTH_TIMEOUTS.SUBSCRIPTION_STALE_TIME,
    gcTime: AUTH_TIMEOUTS.SUBSCRIPTION_CACHE_TIME,
    retry: 1,
  });

  // Derive subscription status
  const currentSubscription =
    subscriptionData &&
    Array.isArray(subscriptionData) &&
    subscriptionData.length > 0
      ? subscriptionData[0]
      : null;

  /**
   * Determine if user has an active subscription.
   *
   * Uses fail-open pattern: assumes subscription is valid when we can't verify.
   * This prevents blocking legitimate users during temporary network issues.
   * Backend always performs authoritative subscription checks.
   */
  const determineSubscriptionStatus = (): boolean => {
    // Non-provider roles don't need subscription
    if (!needsSubscriptionCheck) {
      return true;
    }

    // Network error - fail open to avoid blocking users
    if (subscriptionError) {
      return true;
    }

    // Still loading - fail open while waiting
    if (subscriptionData === undefined) {
      return true;
    }

    // We have data - check actual subscription status
    return currentSubscription?.isActive === true;
  };

  const hasActiveSubscription = determineSubscriptionStatus();

  // Combined logout function
  const logout = useCallback(() => {
    queryClient.clear();
    storeLogout();
    setRole(null);
    auth0Logout();
  }, [auth0Logout, queryClient, storeLogout]);

  // Refresh user data and role
  const refreshUser = useCallback(async (): Promise<boolean> => {
    try {
      await refreshToken();
      // The token refresh will trigger the useEffect that updates user data
      return true;
    } catch (error) {
      logger.error('Failed to refresh user:', error);
      return false;
    }
  }, [refreshToken]);

  return {
    user: storeUser,
    role,
    isAuthenticated: storeIsAuthenticated,
    isLoading: auth0Loading || isProviderLoading,
    login,
    logout,
    error: auth0Error,

    // Non-nullable IDs (empty string if null)
    userId,
    providerId,
    businessOwnerId,

    // Original nullable IDs
    rawUserId,
    rawProviderId,
    rawBusinessOwnerId,

    // Role-based utility functions
    isProvider,
    isShopOwner,
    isCustomer,

    // Access token
    accessToken,

    // Safe API request helper
    withAuth,

    // Refresh user data
    refreshUser,

    // Get new token - for axios interceptor
    getNewToken,

    // Subscription data (pre-fetched for route guards)
    currentSubscription,
    hasActiveSubscription,
    isSubscriptionLoading: needsSubscriptionCheck
      ? isSubscriptionLoading
      : false,
    subscriptionError: subscriptionError as Error | null,
  };
};

export default useAuth;
